import { KEYS, LABELS, STORE_KEYS } from '@constants'
import { saveToStorage } from '@helpers/storage.helpers'
import { Store } from '@stores'
import { ICompany, ICompanyTask, ISingleCompany } from '@typings'
import { action, computed, makeAutoObservable } from 'mobx'
import { toast } from 'react-toastify'

export class CompanyStore {
  [key: string]: any
  loading: boolean
  companies: ISingleCompany[]
  company: null | ISingleCompany
  selectedCompanyId: ISingleCompany['id'] | undefined
  tasks: ICompany['tasks']
  ibanLoading: boolean
  ibanValid: boolean
  ibanError: string | null
  constructor(store: Store) {
    makeAutoObservable(this)
    this.store = store
    this.companies = [] as ISingleCompany[]
    this.company = null
    this.selected = undefined
    this.loading = true
    this.tasks = []
    this.params = {}
    this.ibanLoading = false
    this.ibanValid = false
    this.ibanError = null
  }

  @computed
  get current_company_id(): ISingleCompany['id'] | undefined {
    return this.company?.id
  }

  @computed
  get current_company(): null | ISingleCompany {
    return this.company
  }

  @computed
  get has_company(): boolean {
    return !!this.company
  }

  @computed
  get has_companies(): boolean {
    return !!this.companies.length
  }

  @action
  setSelectedCompany = async (id: ISingleCompany['id']) => {
    this.set(STORE_KEYS.SELECTED, id)
    this.getSingle(id)
  }

  @action
  create = async (data: Partial<ISingleCompany>) => {
    this.set(STORE_KEYS.LOADING, true)

    try {
      const result = await this.store.api.company.createCompany(data)
      console.log('result: ', result)
      this.set(STORE_KEYS.COMPANY, result)
      saveToStorage(KEYS.HAS_COMPANY, true)
      return Promise.resolve(result)
    } catch (e) {
      console.log('error: ', e)
      return Promise.reject(e)
    } finally {
      this.set(STORE_KEYS.LOADING, false)
    }
  }

  @action
  update = async (data: Partial<ISingleCompany>) => {
    this.set(STORE_KEYS.LOADING, true)

    try {
      const result = await this.store.api.company.update(this.current_company?.id, data)
      this.set(STORE_KEYS.COMPANY, result)
      return Promise.resolve(result)
    } catch (e) {
      console.log('error: ', e)
      return Promise.reject(e)
    } finally {
      this.set(STORE_KEYS.LOADING, false)
    }
  }

  @action
  delete = async () => {
    this.set(STORE_KEYS.LOADING, true)
    if (!this.has_company) {
      toast('You have no company!')
      return null
    }
    try {
      console.log('this company id: ', this.company?.id)
      await this.store.api.company.delete(this.company?.id)
      this.set(STORE_KEYS.COMPANY, null)
      return Promise.resolve()
    } catch (e) {
      return Promise.reject(e)
    } finally {
      this.set(STORE_KEYS.LOADING, true)
    }
  }

  @action
  createTask = async (data: ICompanyTask) => {
    this.set(STORE_KEYS.LOADING, true)
    try {
      await this.store.api.company.createTask(data)
      await this.getAllTasks()
      this.store.ui.resetModal()
      return Promise.resolve(data.label)
    } catch (e) {
      console.log('e: ', e)
      return Promise.reject(e)
    } finally {
      this.set(STORE_KEYS.LOADING, false)
    }
  }

  @action
  getAllTasks = async () => {
    this.set(STORE_KEYS.LOADING, true)
    try {
      const result = await this.store.api.company.getAllTasks()
      this.set(STORE_KEYS.TASKS, result)
    } catch (e) {
      console.log('error: ', e)
    } finally {
      this.set(STORE_KEYS.LOADING, false)
    }
  }

  @action
  getSingle = async (id: ISingleCompany['id']) => {
    this.set(STORE_KEYS.LOADING, true)
    try {
      const result = await this.store.api.company.getSingle(id)
      this.set(STORE_KEYS.COMPANY, result)
      Promise.resolve(result)
    } catch (e) {
      console.log('error: ', e)
    } finally {
      this.set(STORE_KEYS.LOADING, false)
    }
  }

  @action
  getMyCompany = async () => {
    this.set(STORE_KEYS.LOADING, true)
    try {
      const result = await this.store.api.company.getMyCompany()
      if (!result.length) {
        saveToStorage(KEYS.HAS_COMPANY, false)
        return
      }
      this.set(STORE_KEYS.COMPANY, result[0])
      saveToStorage(KEYS.HAS_COMPANY, true)
      return Promise.resolve(result[0])
    } catch (e) {
      console.log('error: ', e)
      return Promise.reject(e)
    } finally {
      this.set(STORE_KEYS.LOADING, false)
    }
  }

  @action
  submitSearch = async (params: { query: string; results_per_page?: number }) => {
    // this.set(STORE_KEYS.LOADING, true)
    try {
      const result = await this.store.api.kvk.searchCompany(params)

      // if (params.handelsnaam) {
      //   result.resultaten = KVK.resultaten.filter(item =>
      //     item.handelsnaam.includes(params.handelsnaam as string)
      //   )
      // }
      console.log('result: ', result)
      return result
    } catch (e) {
      console.error(e)
    } finally {
      this.set(STORE_KEYS.LOADING, false)
    }
  }

  @action
  testSmtpConnection = async (smtpSettings: {
    smtp_host: string
    smtp_port: number
    smtp_username: string
    smtp_password: string
    smtp_encryption?: string
  }) => {
    try {
      const result = await this.store.api.company.testSmtpConnection(smtpSettings)
      return Promise.resolve(result)
    } catch (e: any) {
      return Promise.reject(e)
    }
  }

  @action
  validateIban = async (iban: string, setValue: (field: string, value: any, options?: any) => void) => {
    // Reset state at the start
    this.resetIbanValidation()

    // Check if IBAN is empty
    if (!iban || iban.length === 0) {
      this.set('ibanError', 'Voer een IBAN in')
      return
    }

    // Normalize IBAN (remove spaces, uppercase)
    const normalizedIban = iban.replace(/\s/g, '').toUpperCase()

    // Client-side format validation before API call
    const ibanFormatRegex = /^[A-Z]{2}[0-9]{2}[A-Z0-9]{1,30}$/
    if (!ibanFormatRegex.test(normalizedIban)) {
      this.set('ibanError', 'Ongeldig IBAN formaat')
      return
    }

    // Check minimum length
    if (normalizedIban.length < 15) {
      this.set('ibanError', 'IBAN moet minimaal 15 tekens bevatten')
      return
    }

    this.set('ibanLoading', true)

    try {
      const result = await this.store.api.company.validateIban(iban)

      if (result.valid) {
        // Auto-fill bank details
        if (result.bank_name) setValue('company.bank_name', result.bank_name, { shouldDirty: true })
        if (result.bic) setValue('company.bank_bic', result.bic, { shouldDirty: true })
        if (result.iban) setValue('company.bank_iban', result.iban, { shouldDirty: true })

        this.set('ibanValid', true)
        this.set('ibanError', null)
        toast.success(`Bank gevonden: ${result.bank_name || 'Gevalideerd'}`)
      } else {
        this.set('ibanError', result.error || 'Ongeldig IBAN')
        setValue('company.bank_name', '', { shouldDirty: true })
        setValue('company.bank_bic', '', { shouldDirty: true })
      }
    } catch (e: any) {
      // The interceptor returns error.response.data, so check for error field directly
      // A 422 validation error will have { valid: false, error: "message" }
      if (e.error && typeof e.error === 'string') {
        // This is an invalid IBAN response from the API (422)
        // Translate common API error messages to Dutch
        const errorMessage = e.error.toLowerCase().includes('invalid')
          ? 'Ongeldig IBAN'
          : e.error
        this.set('ibanError', errorMessage)
      } else {
        // This is a real service error (network, timeout, 500, etc.)
        this.set('ibanError', 'Validatieservice tijdelijk niet beschikbaar. Probeer het later opnieuw.')
      }
      setValue('company.bank_name', '', { shouldDirty: true })
      setValue('company.bank_bic', '', { shouldDirty: true })
    } finally {
      this.set('ibanLoading', false)
    }
  }

  @action
  resetIbanValidation = () => {
    this.set('ibanValid', false)
    this.set('ibanError', null)
  }

  @action
  deleteLogo = async () => {
    this.set(STORE_KEYS.LOADING, true)
    try {
      await this.store.ui.confirm({
        title: LABELS.LOGO_DELETE_TITLE,
        content: LABELS.LOGO_DELETE_TEXT,
        withCancel: true,
      })

      const result = await this.store.api.company.deleteLogo(this.current_company?.id)
      this.set(STORE_KEYS.COMPANY, result)
      toast.success(LABELS.LOGO_DELETED_SUCCESS)
      return Promise.resolve(result)
    } catch (e) {
      console.log('error: ', e)
      toast.error(LABELS.LOGO_DELETE_FAILED)
      return Promise.reject(e)
    } finally {
      this.set(STORE_KEYS.LOADING, false)
    }
  }

  @action
  set = (target: string, value: unknown, save?: boolean): unknown => {
    if (!target) return
    this[target] = value
    if (save) saveToStorage(target, value)
  }
}
